import { useState, useEffect, useCallback } from 'react';
import sanitizeText from '@/utils/sanitizeText';
import { formatDateTime } from '@/utils/formatDate';
import { 
  FaToggleOn, 
  FaToggleOff, 
  FaCheck, 
  FaTimes,
  FaSync,
  FaSave,
  FaExclamationTriangle,
  FaChevronDown,
  FaChevronRight
} from 'react-icons/fa';
import './MonitoringConfigPanel.scss';
import * as Sentry from '@sentry/react';

/**
 * MonitoringConfigPanel Component
 * Allows configuration of ICMP and SNMP v3 monitoring for a router
 */
const MonitoringConfigPanel = ({ routerId, apiFetch, onStatusChange }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [icmpCollapsed, setIcmpCollapsed] = useState(true);
  const [snmpCollapsed, setSnmpCollapsed] = useState(true);
  const [config, setConfig] = useState({
    icmpEnabled: true,
    icmpIntervalMs: 30000,
    snmpEnabled: false,
    snmpIntervalMs: 60000
  });

  /**
   * Load current monitoring status and configuration
   */
  const loadMonitoringStatus = useCallback(async () => {
    setLoading(true);
    setError(null);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000);

    try {
      const response = await apiFetch(`/api/RouterConfig/${routerId}/monitoring-status`, {
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error('Failed to load monitoring status');
      }

      const data = await response.json();
      if (data.success && data.data) {
        setStatus(data.data);

        // Update config with loaded values
        setConfig({
          icmpEnabled: data.data.icmpEnabled ?? true,
          icmpIntervalMs: data.data.icmpIntervalMs ?? 30000,
          snmpEnabled: data.data.snmpEnabled ?? false,
          snmpIntervalMs: data.data.snmpIntervalMs ?? 60000
        });

        if (onStatusChange) {
          onStatusChange(data.data);
        }
      }
    } catch (err) {
        Sentry.captureException(err);
      if (err.name === 'AbortError') {
        setError('Request timed out');
      } else {
        console.error('Error loading monitoring status:', err);
        setError(err.message || 'Failed to load monitoring status');
      }
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  }, [apiFetch, routerId, onStatusChange]);

  useEffect(() => {
    (async () => { await loadMonitoringStatus(); })();
  }, [routerId, loadMonitoringStatus]);

  /**
   * Start monitoring with current configuration
   */
  const handleStartMonitoring = async () => {
    setSaving(true);
    setError(null);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000);

    try {
      const response = await apiFetch(`/api/RouterConfig/${routerId}/monitoring/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error('Failed to start monitoring');
      }

      const data = await response.json();
      if (data.success) {
        await loadMonitoringStatus();
        alert('Monitoring started successfully. Updates will begin within 30 seconds.');
      } else {
        throw new Error(data.message || 'Failed to start monitoring');
      }
    } catch (err) {
        Sentry.captureException(err);
      if (err.name === 'AbortError') {
        setError('Request timed out');
      } else {
        console.error('Error starting monitoring:', err);
        setError(err.message || 'Failed to start monitoring');
        alert(`Error: ${err.message}`);
      }
    } finally {
      clearTimeout(timeoutId);
      setSaving(false);
    }
  };

  /**
   * Stop monitoring
   */
  const handleStopMonitoring = async () => {
    if (!confirm('Are you sure you want to stop monitoring this router?')) {
      return;
    }

    setSaving(true);
    setError(null);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000);

    try {
      const response = await apiFetch(`/api/RouterConfig/${routerId}/monitoring/stop`, {
        method: 'POST',
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error('Failed to stop monitoring');
      }

      const data = await response.json();
      if (data.success) {
        await loadMonitoringStatus();
        alert('Monitoring stopped successfully.');
      } else {
        throw new Error(data.message || 'Failed to stop monitoring');
      }
    } catch (err) {
        Sentry.captureException(err);
      if (err.name === 'AbortError') {
        setError('Request timed out');
      } else {
        console.error('Error stopping monitoring:', err);
        setError(err.message || 'Failed to stop monitoring');
        alert(`Error: ${err.message}`);
      }
    } finally {
      clearTimeout(timeoutId);
      setSaving(false);
    }
  };

  /**
   * Test connection with current credentials
   */
  const handleTestConnection = async () => {
    setTesting(true);
    setError(null);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    try {
      const response = await apiFetch(`/api/RouterConfig/${routerId}/test-connection`, {
        method: 'POST',
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error('Connection test failed');
      }

      const data = await response.json();
      if (data.success) {
        alert('✅ Connection test successful!\n\nRouter is reachable and responsive.');
      } else {
        alert(`❌ Connection test failed:\n\n${data.error || data.message || 'Unknown error'}`);
      }
    } catch (err) {
        Sentry.captureException(err);
      if (err.name === 'AbortError') {
        alert('❌ Connection test timed out');
      } else {
        console.error('Error testing connection:', err);
        alert(`❌ Connection test failed:\n\n${err.message}`);
      }
    } finally {
      clearTimeout(timeoutId);
      setTesting(false);
    }
  };

  /**
   * Save updated monitoring settings while monitoring stays active.
   * The backend updates the config and bumps LastUpdated, which the
   * background service detects and auto-restarts the monitoring task.
   */
  const handleSaveSettings = async () => {
    setSaving(true);
    setError(null);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000);

    try {
      const response = await apiFetch(`/api/RouterConfig/${routerId}/monitoring/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error('Failed to save monitoring settings');
      }

      const data = await response.json();
      if (data.success) {
        await loadMonitoringStatus();
        alert('Settings saved. Monitoring will restart with updated configuration within 30 seconds.');
      } else {
        throw new Error(data.message || 'Failed to save monitoring settings');
      }
    } catch (err) {
        Sentry.captureException(err);
      if (err.name === 'AbortError') {
        setError('Request timed out');
      } else {
        console.error('Error saving monitoring settings:', err);
        setError(err.message || 'Failed to save monitoring settings');
        alert(`Error: ${err.message}`);
      }
    } finally {
      clearTimeout(timeoutId);
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="monitoring-config-panel loading">
        <div className="spinner"></div>
        <p>Loading monitoring configuration...</p>
      </div>
    );
  }

  const isMonitoring = status?.isMonitoring === true;
  const isAvailable = status?.isAvailable === true;

  return (
    <div className="monitoring-config-panel">
      <div className="panel-header">
        <h3>Monitoring Configuration</h3>
        <div className="status-indicators">
          <div className={`status-badge ${isMonitoring ? 'enabled' : 'disabled'}`}>
            {isMonitoring ? <FaToggleOn /> : <FaToggleOff />}
            {isMonitoring ? 'Monitoring Enabled' : 'Monitoring Disabled'}
          </div>
          {isMonitoring && (
            <div className={`status-badge ${isAvailable ? 'online' : 'offline'}`}>
              {isAvailable ? <FaCheck /> : <FaTimes />}
              {isAvailable ? 'Online' : 'Offline'}
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message">
          <FaExclamationTriangle /> {sanitizeText(error)}
        </div>
      )}

      {status && isMonitoring && (
        <div className="current-status">
          <div className="status-grid">
            <div className="status-item">
              <label>Availability:</label>
              <span className={isAvailable ? 'text-success' : 'text-danger'}>
                {isAvailable ? 'Available' : 'Unavailable'}
              </span>
            </div>
            <div className="status-item">
              <label>Last Successful Ping:</label>
              <span>{formatDateTime(status.lastSuccessfulPing)}</span>
            </div>
            {status.lastError && (
              <div className="status-item full-width">
                <label>Last Error:</label>
                <span className="text-danger">{sanitizeText(status.lastError)}</span>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="config-section collapsible">
        <div 
          className="section-header" 
          onClick={() => setIcmpCollapsed(!icmpCollapsed)}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <h4>
            {icmpCollapsed ? <FaChevronRight /> : <FaChevronDown />}
            {' '}ICMP Monitoring (Ping)
          </h4>
          <div style={{ fontSize: '0.9rem', color: config.icmpEnabled ? '#28a745' : '#6c757d' }}>
            {config.icmpEnabled ? 'Enabled' : 'Disabled'}
          </div>
        </div>
        {!icmpCollapsed && (
          <>
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={config.icmpEnabled}
                  onChange={(e) => setConfig({ ...config, icmpEnabled: e.target.checked })}
                />
                Enable ICMP Monitoring
              </label>
            </div>
            {config.icmpEnabled && (
              <div className="form-group">
                <label>
                  Ping Interval: {(config.icmpIntervalMs / 1000).toFixed(0)} seconds
                </label>
                <input
                  type="range"
                  min="5000"
                  max="300000"
                  step="5000"
                  value={config.icmpIntervalMs}
                  onChange={(e) => setConfig({ ...config, icmpIntervalMs: parseInt(e.target.value) })}
                />
                <small>Range: 5 seconds to 5 minutes</small>
              </div>
            )}
          </>
        )}
      </div>

      <div className="config-section collapsible">
        <div 
          className="section-header" 
          onClick={() => setSnmpCollapsed(!snmpCollapsed)}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <h4>
            {snmpCollapsed ? <FaChevronRight /> : <FaChevronDown />}
            {' '}SNMP v3 Monitoring
          </h4>
          <div style={{ fontSize: '0.9rem', color: config.snmpEnabled ? '#28a745' : '#6c757d' }}>
            {config.snmpEnabled ? 'Enabled' : 'Disabled'}
          </div>
        </div>
        {!snmpCollapsed && (
          <>
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={config.snmpEnabled}
                  onChange={(e) => setConfig({ ...config, snmpEnabled: e.target.checked })}
                />
                Enable SNMP v3 Monitoring
              </label>
            </div>
            {config.snmpEnabled && (
              <div className="form-group">
                <label>
                  SNMP Interval: {(config.snmpIntervalMs / 1000).toFixed(0)} seconds
                </label>
                <input
                  type="range"
                  min="30000"
                  max="600000"
                  step="30000"
                  value={config.snmpIntervalMs}
                  onChange={(e) => setConfig({ ...config, snmpIntervalMs: parseInt(e.target.value) })}
                />
                <small>Range: 30 seconds to 10 minutes</small>
              </div>
            )}
          </>
        )}
      </div>

      <div className="action-buttons">
        <button
          className="btn btn-secondary"
          onClick={handleTestConnection}
          disabled={testing || saving}
        >
          {testing ? <FaSync className="spinning" /> : <FaSync />}
          Test Connection
        </button>
        {!isMonitoring ? (
          <button
            className="btn btn-primary"
            onClick={handleStartMonitoring}
            disabled={saving || (!config.icmpEnabled && !config.snmpEnabled)}
          >
            {saving ? <FaSync className="spinning" /> : <FaCheck />}
            Start Monitoring
          </button>
        ) : (
          <>
            <button
              className="btn btn-primary"
              onClick={handleSaveSettings}
              disabled={saving}
            >
              {saving ? <FaSync className="spinning" /> : <FaSave />}
              Save Settings
            </button>
            <button
              className="btn btn-danger"
              onClick={handleStopMonitoring}
              disabled={saving}
            >
              {saving ? <FaSync className="spinning" /> : <FaTimes />}
              Stop Monitoring
            </button>
          </>
        )}
        <button
          className="btn btn-secondary"
          onClick={loadMonitoringStatus}
          disabled={loading || saving}
        >
          <FaSync className={loading ? 'spinning' : ''} />
          Refresh Status
        </button>
      </div>
    </div>
  );
};

export default MonitoringConfigPanel;
